import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Button, Alert, CircularProgress, Typography,
  Table, TableBody, TableRow, TableCell, Chip, Stack
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PageHeader from '../components/PageHeader';
import JsonViewer from '../components/JsonViewer';
import TokenField from '../components/TokenField';
import api from '../api';

// Claims worth surfacing prominently, with a hint of how to read them.
const TIME_CLAIMS = ['exp', 'iat', 'nbf', 'auth_time'];

const TokenInspector = () => {
  const [claims, setClaims] = useState(null);
  const [idClaims, setIdClaims] = useState(null);
  const [tokens, setTokens] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get('/token/inspect');
      setClaims(data.claims);
      setIdClaims(data.idClaims || null);
      setTokens(data.tokens || null);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const renderValue = (k, v) => {
    if (TIME_CLAIMS.includes(k) && typeof v === 'number') {
      return `${v} (${new Date(v * 1000).toLocaleString()})`;
    }
    return typeof v === 'object' ? JSON.stringify(v) : String(v);
  };

  return (
    <Box>
      <PageHeader
        title="Token Inspector"
        subtitle="Your current session tokens (full) and the decoded claims of the Milient user access token."
        action={<Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : claims ? (
        <Stack spacing={3}>
          {tokens && (
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>Raw tokens</Typography>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  These are full, usable tokens — shown here because this is a testing product. Treat them like passwords.
                </Alert>
                <Stack spacing={2}>
                  <TokenField
                    label="Milient JWT (access token)"
                    value={tokens.access_token}
                    caption="RS256 user token. Verifiable against the gateway /jwks. Paste into jwt.io to inspect."
                  />
                  <TokenField
                    label="Refresh token"
                    value={tokens.refresh_token}
                    caption="Opaque Milient refresh token (not a JWT). Used to mint new access tokens."
                  />
                  <TokenField
                    label="id_token"
                    value={tokens.id_token}
                    caption="OIDC identity token (profile/email claims)."
                  />
                </Stack>
              </CardContent>
            </Card>
          )}
          <Card>
            <CardContent>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                {claims.token_use && <Chip label={`token_use: ${claims.token_use}`} color="primary" />}
                {claims.organization_id && <Chip label={`org: ${claims.organization_id}`} variant="outlined" />}
                {Array.isArray(claims.roles) && claims.roles.map((r) => <Chip key={r} label={r} size="small" />)}
              </Stack>
              <Typography variant="overline" color="text.secondary">Claims</Typography>
              <Table size="small">
                <TableBody>
                  {Object.entries(claims).map(([k, v]) => (
                    <TableRow key={k}>
                      <TableCell sx={{ fontWeight: 600, width: 180, verticalAlign: 'top' }}>{k}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{renderValue(k, v)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          <JsonViewer title="Access token claims (JSON)" data={claims} />
          {idClaims && <JsonViewer title="id_token claims (JSON)" data={idClaims} />}
        </Stack>
      ) : null}
    </Box>
  );
};

export default TokenInspector;
